'use client';

import {
  ResumeUploader,
  type ResumeCompleteResult,
} from 'iq-onboarding/components/resume_uploader';
import {trackResumeSkipped, trackSignup} from 'iq-onboarding/tracking';
import {FileText, PencilLine} from 'lucide-react';
import {useState} from 'react';

interface ResumeBranchProps {
  onComplete: (result: ResumeCompleteResult) => void;
  // Fired once a valid file is selected in the uploader, before upload.
  onFileSelected?: () => void;
}

const trackBranchChoice = (choice: 'upload' | 'manual'): Promise<void> =>
  trackSignup({
    step: 'resume',
    trigger: 'resume_branch_choice',
    action: 'click',
    extra: {choice},
  });

// The first screen of the signup wizard: a zero-effort question instead of a
// forced upload. Visitors with a resume handy get the uploader; everyone else
// goes straight to the manual preference steps.
export function ResumeBranch({onComplete, onFileSelected}: ResumeBranchProps) {
  const [showUploader, setShowUploader] = useState(false);

  if (showUploader) {
    return (
      <div>
        <ResumeUploader onComplete={onComplete} onFileSelected={onFileSelected} />
        <button
          type='button'
          onClick={() => setShowUploader(false)}
          className='mt-4 mx-auto block text-sm text-white/50 hover:text-white/80 transition-colors'>
          &larr; Back
        </button>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-8'>
      <div className='text-center'>
        <h1 className='text-4xl font-extrabold text-white mb-3'>
          Sign up to <span className='text-iq-yellow'>Interview Query</span>
        </h1>
        <h2 className='text-2xl font-bold text-white mt-6 mb-2'>Do you have your resume handy?</h2>
        <p className='text-white/60 text-base'>
          We&apos;ll use it to build your prep plan in seconds &mdash; or just answer a few quick
          questions instead.
        </p>
      </div>

      <div className='flex flex-col gap-4'>
        <button
          type='button'
          onClick={() => {
            void trackBranchChoice('upload');
            setShowUploader(true);
          }}
          className='w-full flex items-center gap-4 border-2 border-white/20 hover:border-iq-orange bg-white/5 hover:bg-iq-orange/10 rounded-2xl p-6 text-left transition-colors'>
          <FileText className='w-8 h-8 text-iq-orange shrink-0' />
          <span>
            <span className='block text-base font-bold text-white'>Yes, upload my resume</span>
            <span className='block text-sm text-white/50 mt-1'>
              We&apos;ll personalize everything for you automatically
            </span>
          </span>
        </button>

        <button
          type='button'
          onClick={() => {
            void trackBranchChoice('manual');
            void trackResumeSkipped('initial');
            onComplete(null);
          }}
          className='w-full flex items-center gap-4 border-2 border-white/20 hover:border-iq-orange bg-white/5 hover:bg-iq-orange/10 rounded-2xl p-6 text-left transition-colors'>
          <PencilLine className='w-8 h-8 text-iq-orange shrink-0' />
          <span>
            <span className='block text-base font-bold text-white'>
              Not right now &mdash; I&apos;ll answer a few quick questions
            </span>
            <span className='block text-sm text-white/50 mt-1'>
              Pick your target roles and companies yourself
            </span>
          </span>
        </button>
      </div>
    </div>
  );
}
